import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where, addDoc } from "firebase/firestore";
import { Helmet } from "react-helmet-async";

import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { db } from "../../firebase";
import feedbackIcon from "../../assets/feedbackIcon.svg";

import useCaseImg1 from "../../assets/useCases/useCaseImg1.png";
import useCaseImg2 from "../../assets/useCases/useCaseImg2.png";
import useCaseImg3 from "../../assets/useCases/useCaseImg3.png";

import useCase1_1 from "../../assets/useCases/icons/useCase1_1.svg";
import useCase1_2 from "../../assets/useCases/icons/useCase1_2.svg";
import useCase1_3 from "../../assets/useCases/icons/useCase1_3.svg";
import useCase1_4 from "../../assets/useCases/icons/useCase1_4.svg";
import useCase2_1 from "../../assets/useCases/icons/useCase2_1.svg";
import useCase2_2 from "../../assets/useCases/icons/useCase2_2.svg";
import useCase2_3 from "../../assets/useCases/icons/useCase2_3.svg";
import useCase2_4 from "../../assets/useCases/icons/useCase2_4.svg";
import useCase3_1 from "../../assets/useCases/icons/useCase3_1.svg";
import useCase3_2 from "../../assets/useCases/icons/useCase3_2.svg";
import useCase3_3 from "../../assets/useCases/icons/useCase3_3.svg";

import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import StraightenIcon from "@mui/icons-material/Straighten";
import LayersIcon from "@mui/icons-material/Layers";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import RoomIcon from "@mui/icons-material/Room";

import "./HomePage.css";

import BookingCalendarPage from "scenes/booking/BookingPage";
import { importAll } from "utils/loadGalleryImages";
import Gallery from "./gallery/Gallery";
import UseCaseCard from "./UseCaseCard";
import { Box } from "@mui/material";

const galleryImages = importAll(
  require.context("../../assets/gallery", false, /\.(png|jpe?g|webp)$/)
);
const useCases = [
  {
    title: "Soirées Privées",
    img: useCaseImg1,
    useCaseData: {
      longDescription:
        "Montez à bord d'un tourbus transformé en salle de fête roulante. Karaoké, poker, anniversaire, team building... nous nous chargeons de l'ambiance, vous n'avez plus qu'à en profiter.",
      inclus: [
        "Chauffeur inclus",
        "Départ/retour à Paris",
        "Capacité : jusqu'à X personnes",
        "Options : sono, lumière, écran, animateur...",
      ],
      tarifs: [
        "700 € pour 3 heures",
        "900 € pour 4 heures",
        "Sur devis pour autres durées ou options",
      ],
      services: [
        { name: "Soirée Karaoké", icon: useCase1_1 },
        { name: "Soirée Poker", icon: useCase1_2 },
        { name: "Soirée Anniversaire", icon: useCase1_3 },
        { name: "Soirée Team Building", icon: useCase1_4 },
      ],
    },
  },
  {
    title: "Voyages & Tournées (24h et plus)",
    img: useCaseImg2,
    useCaseData: {
      longDescription:
        "Le Tourbus devient votre base mobile avec couchages intégrés. Idéal pour les festivals, week-ends, mini-tournées ou hébergements insolites. Profitez d'une expérience unique avec des équipements complets pour vos séjours.",
      inclus: [
        "Transport inclus",
        "Chauffeur professionnel",
        "Couchages jusqu'à X personnes",
        "Départ de Paris (autres villes sur demande)",
      ],
      tarifs: [
        "À partir de 140 € / personne pour 24h",
        "Sur devis selon durée, destination, effectif",
      ],
      services: [
        { name: "Festival + Nuit sur place", icon: useCase2_1 },
        { name: "Tournée d'un groupe de musique", icon: useCase2_2 },
        { name: "EVG/EVJF avec transport + hébergement", icon: useCase2_3 },
        { name: "Transport d'équipe pour 2 à 5 jours", icon: useCase2_4 },
      ],
    },
  },
  {
    title: "Tournages & Loges Mobiles",
    img: useCaseImg3,
    useCaseData: {
      longDescription:
        "Un espace mobile professionnel pour vos projets audiovisuels ou événements pros. Équipé de tout le nécessaire pour le confort de vos équipes et la réussite de vos projets.",
      inclus: [
        "Salon modulable",
        "Éclairage ajustable",
        "Ambiance visuelle unique",
        "Sanitaires",
      ],
      tarifs: ["Sur devis, selon durée, lieu et type d'usage"],
      services: [
        { name: "Clip vidéo, shooting photo", icon: useCase3_1 },
        { name: "Loge mobile (tournage, concert)", icon: useCase3_2 },
        { name: "Pop-up store, showroom itinérant", icon: useCase3_3 },
      ],
    },
  },
];

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down("xs"));

  // useEffect(() => {
  //   window.scrollTo(0, 0);
  // }, []);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setError("");
    setLoading(true);

    if (!email) {
      setError("Veuillez entrer votre email");
      setLoading(false);
      return;
    }

    if (!validateEmail(email)) {
      setError("Veuillez entrer un email valide");
      setLoading(false);
      return;
    }

    try {
      const newsletterRef = collection(db, "bus_newsletter");
      const q = query(newsletterRef, where("email", "==", email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        setSuccessMessage("Vous êtes déjà abonné, merci !");
      } else {
        await addDoc(newsletterRef, { email });
        setSuccessMessage("Merci ! Vous êtes inscrit à la newsletter.");
        setEmail("");
      }
    } catch (err) {
      console.error("Erreur lors de l'inscription :", err);
      setError("Une erreur s'est produite. Veuillez réessayer plus tard.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>TourBusLive – Location de Bus avec Chauffeur à Paris</title>
        <meta
          name="description"
          content="Louez un bus avec chauffeur à Paris pour vos soirées, anniversaires ou événements privés. TourBusLive propose des bus festifs tout confort pour des moments inoubliables."
        />
        <meta
          name="keywords"
          content="location bus Paris, bus anniversaire, bus party, bus événementiel, soirée bus Paris, bus avec chauffeur, louer bus Paris"
        />
        <meta
          property="og:title"
          content="TourBusLive – Bus Party & Événements à Paris"
        />
        <meta
          property="og:description"
          content="Découvrez nos bus événementiels pour fêtes, anniversaires et soirées à Paris. Expérience unique à bord de nos bus avec chauffeur."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://tourbuslive.fr" />
      </Helmet>
      <main>
        {/* Video Section*/}
        <section className="video-section">
          <div className="video-container">
            <video autoPlay muted loop playsInline className="background-video">
              <source
                src="https://filedn.eu/lN0pye8FRgoHewNSirBlYCk/tourbus.mp4"
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </div>

          <div className="content-overlay">
            <div className="content-container">
              <div
                style={{
                  fontFamily: "Sansita One",
                }}
              >
                <h1
                  style={{
                    fontSize: isMobile ? "1.3em" : "4em",
                    padding: isMobile ? "1em" : "0",
                  }}
                >
                  Location de bus avec chauffeur – Bus party Paris – Événements
                  & soirées privées
                </h1>
              </div>
              <button
                className="cta-button"
                onClick={() => navigate("/reserver")}
                style={{
                  background: "#FF353A",
                }}
              >
                Réserver maintenant
              </button>
            </div>
          </div>
        </section>

        {/* useCases Section*/}
        <section
          style={{
            padding: isExtraSmall
              ? "0 0.5rem"
              : isSmall
              ? "0 1rem"
              : isMobile
              ? "0 2.5rem"
              : "0 4rem",
            marginTop: isExtraSmall ? "2em" : isSmall ? "4em" : "10em",
            marginBottom: isExtraSmall ? "3em" : "5em",
          }}
        >
          <h2
            style={{
              fontFamily: "Sansita One",
              fontSize: isExtraSmall ? "1.2em" : isMobile ? "1.5em" : "3em",
              fontWeight: "bold",
              maxWidth: "1200px",
              marginBottom: "0.5em",
              lineHeight: isExtraSmall ? 1.2 : isSmall ? 1.3 : 1.4,
            }}
          >
            Privatisez un Tourbus unique en France pour vos soirées ou vos
            déplacements en groupe !
          </h2>
          <p
            style={{
              fontSize: isExtraSmall ? "0.65em" : isMobile ? "0.75em" : "2em",
              maxWidth: "1450px",
              marginTop: "0.5em",
              lineHeight: isExtraSmall ? 1.4 : isSmall ? 1.5 : 1.6,
            }}
          >
            À la soirée ou pour plusieurs jours, avec ou sans transport, avec ou
            sans couchages : découvrez une expérience événementielle sur-mesure.
          </p>
          <article
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              margin: "0 auto",
              gap: isExtraSmall ? "1.5rem" : isMobile ? "2rem" : "1rem",
              marginTop: isExtraSmall ? "3rem" : isSmall ? "4rem" : "6rem",
            }}
          >
            {useCases.map((caseItem, index) => (
              <>
                <UseCaseCard
                  key={index}
                  img={caseItem.img}
                  title={caseItem.title}
                  description={caseItem.description}
                  useCaseData={caseItem.useCaseData}
                />
                {index < useCases.length - 1 && (
                  <Box
                    sx={{
                      height: "2px",
                      width: "60%",
                      background: "#F8F7F6",
                      mx: "auto",
                      my: {
                        xs: "0.5rem",
                        sm: "1rem",
                        md: "1.5rem",
                        lg: "2.5rem",
                      },
                    }}
                  />
                )}
              </>
            ))}
          </article>

          {/* Specs Section */}
{/* Specs Section */}
{/* Specs Section */}
<section
  style={{
    background: "#F9F9F9",
    padding: isExtraSmall
      ? "2em 1em"
      : isSmall
      ? "3em 2em"
      : "4em 3em",
    margin: "4em auto",
    borderTop: "2px solid #EEE",
    borderBottom: "2px solid #EEE",
  }}
>
  <h2
    style={{
      fontFamily: "Sansita One",
      fontSize: isExtraSmall ? "1.4em" : isMobile ? "1.8em" : "2.5em",
      textAlign: "center",
      color: "#333",
      marginBottom: "1.5em",
    }}
  >
    Fiche technique – Tourbus
  </h2>

  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      gap: "2em",
      maxWidth: "1000px",
      margin: "0 auto",
    }}
  >
    {[
      {
        label: "Modèle",
        value: "Van Hool Astromega T927",
        icon: <DirectionsBusIcon sx={{ fontSize: 40, color: "#d12121" }} />,
      },
      {
        label: "Longueur",
        value: "14 mètres",
        icon: <StraightenIcon sx={{ fontSize: 40, color: "#d12121" }} />,
      },
      {
        label: "Étages",
        value: "Double étage",
        icon: <LayersIcon sx={{ fontSize: 40, color: "#d12121" }} />,
      },
      {
        label: "Chauffeur",
        value: "Professionnel obligatoire",
        icon: <EmojiPeopleIcon sx={{ fontSize: 40, color: "#d12121" }} />,
      },
      {
        label: "Départ",
        value: "Paris (autres lieux sur demande)",
        icon: <RoomIcon sx={{ fontSize: 40, color: "#d12121" }} />,
      },
    ].map((item, idx) => (
      <div
        key={idx}
        style={{
          flex: "1 1 250px",
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
          padding: "1.5em",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {item.icon}
        <h3
          style={{
            fontSize: "1.1em",
            margin: "0.5em 0 0.3em",
            fontWeight: 600,
            color: "#333",
          }}
        >
          {item.label}
        </h3>
        <p style={{ fontSize: "1em", color: "#666", margin: 0 }}>
          {item.value}
        </p>
      </div>
    ))}
  </div>
</section>




        </section>
        {/* gallery Section */}
        <section className="gallery-section" style={{ marginBottom: "3em" }}>
          <h1 className="gallery-title">Notre Galerie</h1>
          <Gallery images={galleryImages} />
          <button
            className="cta-button"
            style={{ minWidth: "15%" }}
            onClick={() => navigate("/galerie")}
          >
            Plus de photos
          </button>
        </section>

        <BookingCalendarPage homePage />

        {/* Feedback Section*/}
        <section
          style={{
            display: "none",
          }}
          className="feedback-section"
        >
          <div className="feedback-title-container">
            <h1 className="feedback-title">Feedback de nos clients</h1>
            <img src={feedbackIcon} alt="feedback icon" />
          </div>

          <div className="feedback-card-container">
            <div className="feedback-card">
              <h1>tommas lechamp</h1>
              <p>
                "Le lieu est magnifique, bien entretenu, et parfaitement équipé
                pour accueillir tout type d&apos;événement. L&apos;ambiance y
                est à la fois conviviale et élégante, ce qui a beaucoup plu à
                nos invités. Mention spéciale à l&apos;équipe sur place,
                toujours disponible, accueillante et très professionnelle!"
              </p>
            </div>
            <div className="feedback-card">
              <h1>Marine LEROUX</h1>
              <p>
                "Le lieu est magnifique, bien entretenu, et parfaitement équipé
                pour accueillir tout type d&apos;événement. L&apos;ambiance y
                est à la fois conviviale et élégante, ce qui a beaucoup plu à
                nos invités. Mention spéciale à l&apos;équipe sur place,
                toujours disponible, accueillante et très professionnelle!"
              </p>
            </div>
          </div>
        </section>

        {/* contact Section */}
        <section className="contact-section">
          <h1>Restons en Contact !</h1>
          <div className="contact-form">
            <input
              type="email"
              placeholder="Votre Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={error ? "error" : ""}
            />
            <button onClick={handleSubmit} disabled={loading}>
              {loading ? <span className="spinner"></span> : "ENVOYER"}
            </button>
          </div>
          {error && <p className="error-message">{error}</p>}
          {successMessage && (
            <p className="success-message">{successMessage}</p>
          )}
        </section>
      </main>
    </>
  );
};

export default Home;
